import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BookOpen, CheckCircle2, ChevronLeft, ChevronRight, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/contexts/AuthContext";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";
import { hasPermission, permissionsForActiveClinic } from "@/lib/permissions";

const guideSteps = [
  { title: "Cadastre a unidade", description: "Confira os dados oficiais, endereço e horário de funcionamento da unidade.", path: "/configuracoes/informacoes-clinica" },
  { title: "Monte a equipe", description: "Cadastre dentistas e funcionários e vincule cada pessoa às unidades corretas.", path: "/cadastros/dentistas" },
  { title: "Cadastre os pacientes", description: "O paciente passa a ficar disponível para agenda, prontuário, documentos e recibos.", path: "/patients" },
  { title: "Configure procedimentos", description: "Informe preço, custo direto e duração para acompanhar agenda e rentabilidade.", path: "/cadastros/honorarios" },
  { title: "Abra a agenda", description: "Com unidade, expediente, dentista e paciente cadastrados, o agendamento já pode ser criado.", path: "/appointments" },
];

const contextualHelp = [
  { match: "/appointments", title: "Agenda", text: "Selecione a unidade ativa. Para agendar, ela precisa ter expediente, paciente e dentista cadastrados." },
  { match: "/patients", title: "Pacientes", text: "Use as abas para acessar ficha clínica, histórico, odontograma, imagens, receitas e recibos da unidade ativa." },
  { match: "/cadastros/patrimonio", title: "Patrimônio", text: "Cadastre a próxima manutenção para receber alertas preventivos no próprio módulo e no dashboard." },
  { match: "/cadastros/honorarios", title: "Procedimentos", text: "O custo direto permite calcular a margem estimada dos atendimentos concluídos." },
  { match: "/financeiro", title: "Financeiro", text: "Os valores e relatórios seguem a unidade selecionada e as permissões do usuário." },
];

export function SystemGuide() {
  const { user } = useAuth();
  const activeClinicaId = useActiveClinicaId();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const storageKey = `odonto.guideSeen.${user?.id || "anonymous"}`;
  const extraPermissions = permissionsForActiveClinic(user?.permissoes_extra, user?.permissoes_por_clinica, activeClinicaId);
  const canConfigure = hasPermission(user?.role || user?.perfil, "clinicas:manage", extraPermissions);
  const steps = canConfigure ? guideSteps : guideSteps.filter((item) => ["/patients", "/appointments"].includes(item.path));

  const context = useMemo(
    () => contextualHelp.find((item) => location.pathname.startsWith(item.match)),
    [location.pathname]
  );

  useEffect(() => {
    if (!user || localStorage.getItem(storageKey) === "1") return;
    const timer = window.setTimeout(() => setOpen(true), 700);
    return () => window.clearTimeout(timer);
  }, [storageKey, user]);

  const closeGuide = () => {
    localStorage.setItem(storageKey, "1");
    setOpen(false);
  };

  const goToStep = () => {
    const target = steps[step];
    closeGuide();
    navigate(target.path);
  };

  return (
    <>
      <Button variant="ghost" size="icon" title="Ajuda e primeiros passos" onClick={() => setOpen(true)}>
        <HelpCircle className="h-4 w-4" />
        <span className="sr-only">Ajuda e primeiros passos</span>
      </Button>
      <Dialog open={open} onOpenChange={(value) => value ? setOpen(true) : closeGuide()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              {context?.title || "Primeiros passos"}
            </DialogTitle>
            <DialogDescription>
              {context?.text || "Uma configuração simples e segura para começar a operar a clínica."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Progress value={((step + 1) / steps.length) * 100} className="h-2" />
            <div className="rounded-md border bg-muted/30 p-4">
              <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                Etapa {step + 1} de {steps.length}
              </div>
              <h3 className="font-semibold">{steps[step].title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{steps[step].description}</p>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button variant="outline" size="sm" disabled={step === 0} onClick={() => setStep((value) => value - 1)}>
                <ChevronLeft className="mr-1 h-4 w-4" /> Voltar
              </Button>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={closeGuide}>Fechar</Button>
                {step < steps.length - 1 ? (
                  <Button size="sm" onClick={() => setStep((value) => value + 1)}>
                    Próxima <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                ) : (
                  <Button size="sm" onClick={goToStep}>Abrir agenda</Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
